<template>
  <div class="gm-pacing">
    <span class="gm-pacing__label">Pacing</span>
    <template v-if="enoughHistory">
      <el-popover trigger="hover" placement="bottom" :open-delay="200">
        <div class="popover-body gm-pacing__popover">{{ popoverText }}</div>
        <span slot="reference" class="gm-pacing__status" :class="'gm-pacing__status--' + status">{{ label }}</span>
      </el-popover>
      <span v-if="projectionText" class="gm-pacing__projection">{{ projectionText }}</span>
    </template>
    <template v-else>
      <span class="gm-pacing__status gm-pacing__status--unknown">Not enough history yet ({{ sampleSize }} of {{ minSample }} events)</span>
      <span v-if="projectionText" class="gm-pacing__projection">{{ projectionText }}</span>
    </template>
    <small v-if="timerPaused" class="gm-pacing__note">Timer paused, pacing continues</small>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatDuration, statusLabel } from '@/util/trackerPacing';

const props = defineProps({
  status: { type: String, default: '' },
  projectedFinish: { type: Number, default: null },
  projectedDelta: { type: Number, default: null },
  sampleSize: { type: Number, default: 0 },
  enoughHistory: { type: Boolean, default: false },
  timerPaused: { type: Boolean, default: false },
  minSample: { type: Number, default: 5 },
});

const label = computed(() => statusLabel(props.status));

const projectionText = computed(() => {
  if (props.status === 'finished' || props.projectedFinish === null || props.projectedFinish === undefined) {
    return '';
  }
  let text = 'Projected finish ' + formatDuration(props.projectedFinish);
  if (props.projectedDelta !== null && props.projectedDelta !== undefined && props.projectedDelta > 0) {
    text += ', ' + formatDuration(props.projectedDelta) + ' over';
  }
  return text;
});

const popoverText = computed(() =>
  'Pacing compares this team with the last ' + props.sampleSize +
  ' finished events. Times count from the event start time on the Group step.'
);
</script>

<style scoped>
.gm-pacing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
}

.gm-pacing__label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 4px;
}

.gm-pacing__status {
  font-size: 16px;
  font-weight: 700;
  cursor: default;
}

.gm-pacing__status--ahead { color: #00bf9a; }
.gm-pacing__status--on_pace { color: #1d8cf8; }
.gm-pacing__status--behind { color: #ec250d; }
.gm-pacing__status--finished { color: rgba(255, 255, 255, 0.6); }
.gm-pacing__status--unknown {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.gm-pacing__projection {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-variant-numeric: tabular-nums;
}

.gm-pacing__note {
  margin-top: 3px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.55);
}

.gm-pacing__popover {
  max-width: 260px;
}
</style>

<style>
.white-content .gm-pacing__label {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .gm-pacing__status--finished,
.white-content .gm-pacing__status--unknown,
.white-content .gm-pacing__projection,
.white-content .gm-pacing__note {
  color: rgba(0, 0, 0, 0.5);
}
</style>
